import { CalendarMinus, Download, FileText } from "lucide-react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { useState } from "react";
import Pageable from "../model/page/Pageable";
import ContaPagar from "../model/contaPagar/ContaPagar";
import BoletoService from "../service/financeiro/BoletoService";
import GerarSegundaViaBoletoSienge from "../model/sienge/boleto/GerarSegundaViaBoletoSienge";
import { useToastContext } from "../provider/ToastContext";
import { localDateStringToDate } from "../utils/convert";
import SimulacaoAntecipacaoDialog from "./dialog/SimulacaoAntecipacaoDialog";
import GerarBoletoResultSienge from "../model/sienge/boleto/GerarBoletoResultSienge";
import BoletoDialog from "./dialog/BoletoDialog";
import { useLoadingContext } from "../provider/LoadingProvider";

type Props = {
  itens: ContaPagar[];
}

const pageSize = 20;
const boletoService = new BoletoService();
const primeiroDiaMes = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
const inicio = new Date(2025, 11, 19);
const fim = new Date(2026, 0, 7, 23, 59, 59);
export function ContasPagarComponent({ itens }: Props) {

  const [totalElements, setTotalElements] = useState<number>(0);
  const [pageable, setPageable] = useState(new Pageable(0, pageSize, "", 0));

  const [itensSelecionados, setItensSelecionados] = useState<ContaPagar[]>([]);
  const [showModalSimulacao, setShowModalSimulacao] = useState<boolean>(false);
  const [modalBoleto, setModalBoleto] = useState<{ visible: boolean, boleto: GerarBoletoResultSienge | null, contaPagar: ContaPagar | null }>({ visible: false, boleto: null, contaPagar: null });

  const { msgErro } = useToastContext();
  const { startLoading, stopLoading } = useLoadingContext();


  const onPage = (event: DataTableStateEvent) => {
    setPageable({ ...pageable, page: event.page !== undefined ? event.page : 0, first: event.first });
  };

  const documentoTemplate = (rowData: ContaPagar) => {
    return rowData.documento;
  };

  const parcelaTemplate = (rowData: ContaPagar) => {
    return rowData.numeroParcela;
  };

  const vencimentoTemplate = (rowData: ContaPagar) => {
    return localDateStringToDate(rowData.vencimento as unknown as string).toLocaleDateString("pt-BR");
  };

  const valorTemplate = (rowData: ContaPagar) => {
    return rowData.valorAtual.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  const acoesTemplate = (rowData: ContaPagar) => {
    return (
      <>
        {rowData.boletoGerado && localDateStringToDate(rowData.vencimento).getTime() >= primeiroDiaMes.getTime() ? <Button
          icon={<FileText size={16} />}
          label="Boleto"
          className="p-button-outlined p-button-sm"
          onClick={() => {
            verDadosBoleto(rowData);
          }}
        /> : null}
      </>
    );
  };

  function verDadosBoleto(contaPagar: ContaPagar) {
    startLoading();
    boletoService.gerarSegundaVia(new GerarSegundaViaBoletoSienge(contaPagar.billReceivableId, contaPagar.installmentId))
      .then((response) => {
        setModalBoleto({ visible: true, boleto: response, contaPagar });
      })
      .catch((err) => {
        msgErro("Erro ao gerar Boleto", err?.data?.message || 'Erro ao gerar segunda via do boleto.');
        console.log(err);
      }).finally(() => {
        stopLoading();
      });
  }

  const header = <div style={{ alignItems: "center", display: "flex", justifyContent: "space-between" }}>
    <span>Contas a pagar</span>
    <Button
      label="Simular antecipação"
      className="p-button-sm p-button-outlined"
      icon={<CalendarMinus size={16} />}
      onClick={() => simularAntecipacao()} />
  </div>

  function simularAntecipacao() {
    if (itensSelecionados.length === 0) {
      msgErro("Seleção inválida", "Selecione ao menos uma conta para simular a antecipação.");
      return;
    }
    if (itensSelecionados.filter(item => item.boletoGerado).length > 0) {
      msgErro("Seleção inválida", "Apenas contas sem boleto gerado podem ser antecipadas.");
      return;
    }

    const billId = itensSelecionados[0].billReceivableId;
    if (itensSelecionados.some(item => item.billReceivableId !== billId)) {
      msgErro("Seleção inválida", "Todas as contas selecionadas devem pertencer à mesma fatura.");
      return;
    }
    setShowModalSimulacao(true);
  }

  return (
    <div>
      {showModalSimulacao ? <SimulacaoAntecipacaoDialog visible={showModalSimulacao} onHide={() => setShowModalSimulacao(false)} contasPagar={itensSelecionados} onConfirm={() => {
        setShowModalSimulacao(false);
        setItensSelecionados([]);
      }} /> : null}

      {modalBoleto.visible ? <BoletoDialog
        visible={modalBoleto.visible}
        onHide={() => setModalBoleto({ visible: false, boleto: null, contaPagar: null })}
        boleto={modalBoleto.boleto}
        contaPagar={modalBoleto.contaPagar} /> : null}

      <Card title="Suas Faturas" subTitle="Lista de todas as suas contas a pagar" >
        <DataTable
          header={header}
          value={itens}
          paginator
          first={pageable.first} rows={pageSize}
          totalRecords={totalElements}
          onPage={onPage}
          stripedRows
          selectionMode={"multiple"}
          selection={itensSelecionados}
          onSelectionChange={(e) => {
            const list = e.value.sort((a: ContaPagar, b: ContaPagar) => a.installmentId - b.installmentId);
            console.log(list);
            setItensSelecionados(list);
          }}
          emptyMessage="Nenhuma conta a pagar encontrada."
        >
          <Column selectionMode="multiple" headerStyle={{ width: '3rem' }}></Column>
          <Column header="Documento" body={documentoTemplate} />
          <Column header="Parcela" body={parcelaTemplate} />
          <Column header="Vencimento" body={vencimentoTemplate} />
          <Column header="Valor" body={valorTemplate} />
          <Column header="Ações" body={acoesTemplate} style={{ width: 300 }} />
        </DataTable>
      </Card>
    </div>
  );
}
